import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ViolationReturn } from '../clients/rust-api-client';
import { violationService } from '../clients/ApiClients';
import { KanbanBoard, KanbanColumnConfig } from '../common/Custom/Kanban';
import { Badge } from '../common/Catalyst/badge';
import ShowAlert from '../ShowAlert';
import { formatMoney } from '../utils/billingHelpers';
import { fineStatus } from '../billing/enumLabels';
import { useSyncedViolations } from '../sync/hooks';
import { useFreshFines } from '../sync/fines';
import { updateViolation } from '../sync/writes';
import { canEditViolation } from '../constants/UserTypes';
import { getUserTypeFromToken } from '../utils/TokenUtils';
import { apiErrorMessage } from '../utils/apiErrors';

// Status values must match API responses (PascalCase)
type ViolationStatus = 'Pending' | 'InProgress' | 'Resolved' | 'Escalated' | 'Appealed';

const violationColumns: KanbanColumnConfig<ViolationStatus>[] = [
  { status: 'Pending', label: 'Pending', color: 'yellow' },
  { status: 'InProgress', label: 'In Progress', color: 'blue' },
  { status: 'Escalated', label: 'Escalated', color: 'orange' },
  { status: 'Appealed', label: 'Appealed', color: 'purple' },
  { status: 'Resolved', label: 'Resolved', color: 'green' },
];

const severityColors: Record<string, 'red' | 'orange' | 'yellow' | 'green' | 'zinc'> = {
  critical: 'red',
  major: 'orange',
  moderate: 'yellow',
  minor: 'green',
};

interface ViolationKanbanBoardProps {
  hoaId?: string | null;
  homeId?: string | null;
  /** Fill the parent's width and height (the violations page). */
  fill?: boolean;
  /**
   * A bar over the board, laid out like the list's filter bar, with this
   * at its right end: the view switch, in the same place in both views.
   */
  toolbar?: React.ReactNode;
  /**
   * With `fill`: the toolbar becomes an edge-to-edge top row the same
   * height as the list's filter bar, so the view switch does not move
   * when the view changes, and the columns get their own gutter.
   */
  flush?: boolean;
}

export function ViolationKanbanBoard({
  hoaId,
  homeId,
  fill = false,
  toolbar,
  flush = false,
}: ViolationKanbanBoardProps): React.ReactElement {
  const navigate = useNavigate();
  const [fetchedViolations, setViolations] = useState<ViolationReturn[]>([]);
  // From the sync engine's copy when there is one (#561). A dropped card's
  // new status is laid over it by the write queue until the server echoes
  // it, so the card does not jump back in between.
  const synced = useSyncedViolations({ hoaId, homeId });
  const haveSynced = synced !== null;
  const violations = synced ?? fetchedViolations;
  useFreshFines({ hoaId });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchViolations = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // Same scoping as the table view: one HOA when we have one,
        // otherwise everything this role is entitled to see.
        const response = hoaId
          ? await violationService.getViolationsByHoa({ hoaId })
          : await violationService.getAllViolationsForUser();
        let allViolations = response.data.violations;

        // Filter by homeId if provided
        if (homeId) {
          allViolations = allViolations.filter((v) => v.home_id === homeId);
        }

        setViolations(allViolations);
        setError(null);
      } catch (err) {
        console.error('Error fetching violations:', err);
        setError(apiErrorMessage(err, "Couldn't load violations."));
        setShowError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchViolations();
  }, [hoaId, homeId, haveSynced]);

  const handleStatusChange = async (violationId: string, newStatus: ViolationStatus): Promise<void> => {
    const violation = violations.find((v) => v.id === violationId);
    if (!violation) return;

    try {
      const saved = await updateViolation(violationId, { status: newStatus });
      // Without a copy, the board shows what it fetched.
      if (saved) {
        setViolations((prev) => prev.map((v) => (v.id === violationId ? saved : v)));
      }
    } catch (err) {
      console.error('Error updating violation status:', err);
      setError(apiErrorMessage(err, "Couldn't update the status."));
      setShowError(true);
      throw err; // Re-throw to trigger optimistic update rollback
    }
  };

  const handleCardClick = (violationId: string) => {
    navigate(`/violations/${violationId}`);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const renderViolationCard = (violation: ViolationReturn, isDragging?: boolean) => (
    <div
      onClick={() => !isDragging && handleCardClick(violation.id)}
      className="bg-white dark:bg-zinc-800 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-3 hover:shadow-md transition-shadow duration-200 cursor-pointer"
    >
      {/* Type */}
      <h4 className="font-medium text-sm text-zinc-900 dark:text-zinc-100 mb-2">
        {violation.violation_type}
      </h4>

      {/* Severity */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge color={severityColors[violation.severity.toLowerCase()] || 'zinc'}>
          {violation.severity}
        </Badge>
        {violation.fine && (
          <Badge color={fineStatus.color(violation.fine.status)}>
            {formatMoney(violation.fine.amount)}
          </Badge>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-2">
        {violation.description}
      </p>

      {/* Dates */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs text-zinc-500 dark:text-zinc-400 mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-700">
        <span>Reported: {formatDate(violation.date_of_violation)}</span>
        {violation.due_date && (
          <span className="text-orange-600 dark:text-orange-400">
            Due: {formatDate(violation.due_date)}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className={fill ? 'flex h-full min-h-0 flex-col' : undefined}>
      {toolbar && (
        <div
          className={
            flush
              ? 'flex min-h-[3.25rem] items-center justify-end gap-3 border-b border-line/60 px-3 py-2'
              : 'mb-3 flex min-h-[2.75rem] items-center justify-end gap-3 rounded-lg border border-line bg-raised px-3 py-1.5'
          }
        >
          <span className="text-[12px] text-fg-muted" aria-live="polite">
            {violations.length}
          </span>
          {toolbar}
        </div>
      )}
      {/* Flush against the panel's edges, the alert needs its own inset. */}
      <div className={flush ? 'px-3 pt-3 empty:hidden' : undefined}>
        <ShowAlert
          alertType="error"
          alertTitle="Error"
          alertMessage={error || 'An error occurred'}
          isVisible={showError}
          onClose={() => setShowError(false)}
          timeout={5000}
          isInline={true}
        />
      </div>
      <div className={flush ? 'flex min-h-0 flex-1 flex-col p-4 lg:p-6' : 'contents'}>
        <KanbanBoard
          items={violations}
          columns={violationColumns}
          getItemStatus={(item) => item.status as ViolationStatus}
          getItemId={(item) => item.id}
          renderCard={renderViolationCard}
          onStatusChange={handleStatusChange}
          // Dragging into Resolved closes a violation, which only the
          // management company may do (#116).
          readOnly={!canEditViolation(getUserTypeFromToken())}
          isLoading={loading && !haveSynced}
          fill={fill}
        />
      </div>
    </div>
  );
}

export default ViolationKanbanBoard;
